import { Controller } from "@hotwired/stimulus"

const ALLOWED_TYPES = [
  "image/jpeg", "image/png", "image/gif", "image/webp",
  "application/pdf", "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
]

const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25MB

// Connects to data-controller="message-form"
export default class extends Controller {
  static targets = ["preview", "fileInput", "content"]
  static values = { typingUrl: String }

  connect() {
    this.selectedFiles = []
    this.typingTimeout = null
    this.lastTypingSent = 0
  }

  typing() {
    // Only send typing event if we have a typing URL
    if (!this.typingUrlValue) return

    // Debounce: don't send more than once every 2 seconds
    const now = Date.now()
    if (now - this.lastTypingSent < 2000) return

    this.lastTypingSent = now

    // Send typing event
    fetch(this.typingUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        "Accept": "text/vnd.turbo-stream.html"
      }
    }).catch(() => {
      // Silently ignore errors
    })
  }

  previewFiles(event) {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    const rejected = []
    const accepted = []

    files.forEach(file => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        rejected.push(`${file.name} (unsupported format)`)
      } else if (file.size > MAX_FILE_SIZE) {
        rejected.push(`${file.name} (exceeds 25MB)`)
      } else {
        accepted.push(file)
      }
    })

    if (rejected.length > 0) {
      alert(`The following files were skipped:\n\n${rejected.join("\n")}\n\nSupported: images (JPEG, PNG, GIF, WebP) and documents (PDF, DOC, DOCX) under 25MB.`)
    }

    if (accepted.length === 0) {
      // Reset the file input
      this.fileInputTarget.value = ""
      return
    }

    // Update file input with only accepted files
    this.selectedFiles = accepted
    const dt = new DataTransfer()
    accepted.forEach(file => dt.items.add(file))
    this.fileInputTarget.files = dt.files

    this.previewTarget.innerHTML = ""
    this.previewTarget.classList.remove("hidden")

    accepted.forEach((file, index) => {
      const previewItem = this.createPreviewItem(file, index)
      this.previewTarget.appendChild(previewItem)
    })
  }

  createPreviewItem(file, index) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative group"
    wrapper.dataset.index = index

    if (file.type.startsWith("image/")) {
      const img = document.createElement("img")
      img.className = "h-16 w-16 object-cover rounded-lg border border-gray-200"
      img.src = URL.createObjectURL(file)
      wrapper.appendChild(img)
    } else {
      const docIcon = document.createElement("div")
      docIcon.className = "h-16 w-16 flex flex-col items-center justify-center bg-gray-100 rounded-lg border border-gray-200"
      docIcon.innerHTML = `
        <svg class="w-6 h-6 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
        <span class="text-xs text-gray-500 truncate w-14 text-center mt-1">${this.truncateFilename(file.name)}</span>
      `
      wrapper.appendChild(docIcon)
    }

    // Remove button
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
    removeBtn.innerHTML = "&times;"
    removeBtn.addEventListener("click", () => this.removeFile(index))
    wrapper.appendChild(removeBtn)

    return wrapper
  }

  truncateFilename(name) {
    if (name.length <= 8) return name
    const ext = name.split(".").pop()
    return name.substring(0, 5) + "..." + ext
  }

  removeFile(index) {
    this.selectedFiles.splice(index, 1)

    // Rebuild the file input with remaining files
    const dt = new DataTransfer()
    this.selectedFiles.forEach(file => dt.items.add(file))
    this.fileInputTarget.files = dt.files

    // Rebuild preview
    if (this.selectedFiles.length === 0) {
      this.previewTarget.classList.add("hidden")
      this.previewTarget.innerHTML = ""
    } else {
      this.previewTarget.innerHTML = ""
      this.selectedFiles.forEach((file, i) => {
        const previewItem = this.createPreviewItem(file, i)
        this.previewTarget.appendChild(previewItem)
      })
    }
  }

  submit(event) {
    // Clear preview after successful submission
    this.previewTarget.classList.add("hidden")
    this.previewTarget.innerHTML = ""
    this.selectedFiles = []
  }
}
